import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ApiClient } from '../services/api';
import { env } from '../config/env';
import type { StudentCourse, StudentDetailResponse } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { usePartnerScope } from '../contexts/PartnerScopeContext';
import { SelectPartnerPrompt } from '../components/SelectPartnerPrompt';

const PURPLE = '#250e62';
const LIGHT_COOL_GREY = '#EFF3F5';

type SortColumn = 'course_title' | 'percent_completed' | 'certification_date';
type SortOrder = 'asc' | 'desc';

function StatusChip({ state }: { state: string }): React.ReactElement | null {
  if (state === 'complete_and_certified') {
    return (
      <Chip
        label="Certified"
        size="small"
        sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600, fontSize: '0.75rem' }}
      />
    );
  }
  if (state === 'active' || state === 'active_fastpath' || state === 'partial_active') {
    return (
      <Chip
        label="In Progress"
        size="small"
        sx={{ bgcolor: '#EDE7F6', color: '#250e62', fontWeight: 600, fontSize: '0.75rem' }}
      />
    );
  }
  if (state === 'not_registered') {
    return (
      <Chip
        label="Not Started"
        size="small"
        sx={{ bgcolor: '#EFF3F5', color: '#546E7A', fontWeight: 600, fontSize: '0.75rem' }}
      />
    );
  }
  return null;
}

function ProgressCell({ percent }: { percent: number }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
      <LinearProgress
        variant="determinate"
        value={Math.min(percent, 100)}
        sx={{
          flex: 1,
          height: 6,
          borderRadius: 3,
          bgcolor: '#EFF3F5',
          '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: '#250e62' },
        }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 36, textAlign: 'right' }}>
        {Math.round(percent)}%
      </Typography>
    </Box>
  );
}

export function StudentDetail(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const { idToken } = useAuth();
  const { activePartnerId: partnerId } = usePartnerScope();
  const PARTNER_ID = partnerId ?? 0;
  const studentApi = useMemo(() => new ApiClient(idToken, env.studentApiUrl), [idToken]);

  const [detail, setDetail] = useState<StudentDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [sortColumn, setSortColumn] = useState<SortColumn>('certification_date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  useEffect(() => {
    if (!id || partnerId == null) return;
    const userId = Number(id);

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const detailRes = await studentApi.getStudentDetail(PARTNER_ID, userId);
        setDetail(detailRes);
      } catch (err) {
        console.error('[StudentDetail] Failed to fetch:', err);
        setError(err instanceof Error ? err.message : 'Failed to load student details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, studentApi, partnerId]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder(column === 'course_title' ? 'asc' : 'desc');
    }
  };

  const sortedCourses = React.useMemo(() => {
    if (!detail) return [];
    const items = detail.courses.filter((c) => c.state !== 'will_not_complete');
    items.sort((a, b) => {
      let aVal: string | number = '';
      let bVal: string | number = '';

      switch (sortColumn) {
        case 'course_title':
          aVal = a.course_title.toLowerCase();
          bVal = b.course_title.toLowerCase();
          break;
        case 'percent_completed':
          aVal = a.percent_completed || 0;
          bVal = b.percent_completed || 0;
          break;
        case 'certification_date':
          aVal = a.certification_date || '';
          bVal = b.certification_date || '';
          break;
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return items;
  }, [detail, sortColumn, sortOrder]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const cardSx = {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 2,
    p: 2.5,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 2,
  };

  if (partnerId == null) {
    return <SelectPartnerPrompt />;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !detail) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Student not found'}</Alert>
      </Container>
    );
  }

  const { student, summary } = detail;

  return (
    <>
      {/* Header band — cool grey */}
      <Box sx={{ bgcolor: 'background.default' }}>
        <Container maxWidth="xl" sx={{ pt: 3, pb: 3 }}>
          <Typography
            variant="body2"
            onClick={() => navigate('/students')}
            sx={{
              color: 'primary.main',
              cursor: 'pointer',
              mb: 2,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            <i className="fa-thin fa-arrow-left" style={{ fontSize: 14 }} />
            Back to Students
          </Typography>

          <Typography variant="h1" gutterBottom>
            {student.last_name}, {student.first_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {student.email}
          </Typography>
        </Container>
      </Box>

      {/* Content area — white */}
      <Box sx={{ bgcolor: 'background.paper', flex: 1 }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {/* At-a-glance summary cards */}
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {[
              {
                label: 'Total Courses',
                value: summary.total_courses,
                icon: 'fa-thin fa-book-open',
              },
              {
                label: 'Certified',
                value: summary.completed,
                icon: 'fa-thin fa-circle-check',
              },
              {
                label: 'In Progress',
                value: summary.in_progress,
                icon: 'fa-thin fa-spinner',
              },
            ].map((stat) => (
              <Grid item xs={6} md={3} key={stat.label}>
                <Box sx={cardSx}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      bgcolor: LIGHT_COOL_GREY,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <i className={stat.icon} style={{ fontSize: 20, color: PURPLE }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, color: PURPLE, lineHeight: 1.1 }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5, fontSize: '0.8125rem' }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Course progress table */}
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortColumn === 'course_title'}
                        direction={sortColumn === 'course_title' ? sortOrder : 'asc'}
                        onClick={() => handleSort('course_title')}
                      >
                        Course
                      </TableSortLabel>
                    </TableCell>
                    {!isSmall && <TableCell>Code</TableCell>}
                    {!isSmall && <TableCell align="right">Credits</TableCell>}
                    <TableCell>Status</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortColumn === 'percent_completed'}
                        direction={sortColumn === 'percent_completed' ? sortOrder : 'asc'}
                        onClick={() => handleSort('percent_completed')}
                      >
                        Progress
                      </TableSortLabel>
                    </TableCell>
                    {!isSmall && <TableCell align="right">Score</TableCell>}
                    <TableCell>
                      <TableSortLabel
                        active={sortColumn === 'certification_date'}
                        direction={sortColumn === 'certification_date' ? sortOrder : 'asc'}
                        onClick={() => handleSort('certification_date')}
                      >
                        Certified
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedCourses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isSmall ? 5 : 7} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary">No courses found.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedCourses.map((c: StudentCourse) => (
                      <TableRow key={c.course_id} hover>
                        <TableCell>{c.course_title}</TableCell>
                        {!isSmall && (
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {c.course_code}
                            </Typography>
                          </TableCell>
                        )}
                        {!isSmall && (
                          <TableCell align="right">
                            {Math.round(parseFloat(c.credits || '0'))}
                          </TableCell>
                        )}
                        <TableCell>
                          <StatusChip state={c.state} />
                        </TableCell>
                        <TableCell>
                          <ProgressCell percent={c.percent_completed || 0} />
                        </TableCell>
                        {!isSmall && (
                          <TableCell align="right">
                            {c.certification_date && c.final_score != null
                              ? `${c.final_score}%`
                              : '--'}
                          </TableCell>
                        )}
                        <TableCell>{formatDate(c.certification_date)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Container>
      </Box>
    </>
  );
}
